import { PayloadAction, createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';
import { mockCustomerProducts } from '../../constantsValues/MockCustomerProducts';
import { apiInstance } from '../../services/Api';

type PolicyStatus = 'PENDING' | 'ACTIVE' | 'EXPIRED' | 'CANCELLED';
type InsuranceGroup = 'health' | 'family' | 'vehicles' | 'housing';
type PaymentFrequency = 'EVERY_YEAR' | 'EVERY_MONTH';

const validPolicyStates: PolicyStatus[] = ['PENDING', 'ACTIVE'];

export enum CustomerProducts {
	HOUSE = 'house',
	TRAVEL = 'travel',
	CAR = 'car',
	DOG = 'dog',
	TRAILER = 'trailer',
	ACCIDENT = 'accident',
	HOME = 'home',
	CARAVAN = 'caravan',
	PLEASURE_CRAFT = 'pleasure_craft', // Lystfartøj
	MOPED = 'moped',
	HOLIDAY_HOUSE = 'holidayhouse',
	// MISSING:
	// Husforsikring - Nedlagt landbrug
	// Motorcykelforsikring
	// Kolonihavehusforsikring
	// Påhængsvognsforsikring
	// Bilforsikring Køretøj Special
	// Børneulykkesforsikring, Sundhedsforsikring eller Udvidet strakserstatning.
}

interface ProductApi {
	id: string;
	policy_number: string;
	type: CustomerProducts;
	status: {
		subtitle: string;
		type: PolicyStatus;
	};
	group: {
		type: InsuranceGroup;
	};
	title: string;
	subtitle: string;
	price: {
		amount: number;
		charged: PaymentFrequency;
	};
	currentPolicyStatus: PolicyStatus;
}

interface ProductApiResponse {
	policies: ProductApi[];
}
export interface CustomerProductsDetail {
	currentPolicyStatus: PolicyStatus;
	policyNumber?: string;
	price?: number;
	charged?: PaymentFrequency;
	title?: string;
	subtitle?: string;
	group?: InsuranceGroup;
}
export interface CustomerProductsState {
	uniqueProducts?: string[];
	products?: CustomerProductsDetail[];
	isProductDataFetched: boolean;
	loading?: boolean;
	error?: any;
}

const initialState: CustomerProductsState = {
	isProductDataFetched: false,
	loading: false,
	error: null,
};

export const loadCustomerProducts = createAsyncThunk(
	// Own action type: sharing loadCustomer's type made both slices react to each other's pending/fulfilled/rejected.
	'customerProducts/loadCustomerProducts',
	async (metaArea: MetaArea, { rejectWithValue }) => {
		try {
			const ref = location.href.toLowerCase();
			// possible to mock customer on test environments -> add query param "customer=1"
			if (ref.includes('customer=') && (ref.startsWith('https://localhost') || ref.startsWith('https://sit-'))) {
				return await mockCustomerProducts();
			}

			const productUrls = metaArea?.items?.[0]?.content;
			const axios = apiInstance();

			const auth: any = await axios.get(productUrls?.tokenExpireApiEndpoint?.url);
			if (auth?.authenticated) {
				const res = await axios.get(productUrls?.customerProductsApiEndpoint?.url);
				return res;
			}
			return rejectWithValue({ authenticated: false });
		} catch (error) {
			const formattedError = error as AxiosError;
			if (!formattedError?.response) {
				throw error;
			}
			const { data, status } = formattedError.response;
			return rejectWithValue({ data, status });
		}
	}
);

export const customerProductsSlice = createSlice({
	name: 'CustomerProductsSlice',
	initialState,
	reducers: {
		setCustomerProducts: (state, action: PayloadAction<ProductApiResponse>) => {
			const uniqueProducts: Set<string> = new Set();
			const products: CustomerProductsDetail[] = [];
			for (const policy of action.payload?.policies || []) {
				if (validPolicyStates.includes(policy.currentPolicyStatus)) {
					uniqueProducts.add(policy.type);

					products.push({
						currentPolicyStatus: policy.currentPolicyStatus,
						policyNumber: policy.policy_number,
						price: policy.price?.amount,
						charged: policy.price?.charged,
						title: policy.title,
						subtitle: policy.subtitle,
						group: policy.group?.type,
					});
				}
			}

			state = { ...state, uniqueProducts: Array.from(uniqueProducts), products };
			return state;
		},
	},
	extraReducers: (builder) => {
		builder.addCase(loadCustomerProducts.pending, (state) => {
			state.loading = true;
			state.error = null;
		});
		builder.addCase(loadCustomerProducts.fulfilled, (state) => {
			state.loading = false;
			state.isProductDataFetched = true;
			state.error = null;
		});
		builder.addCase(loadCustomerProducts.rejected, (state, action) => {
			state.loading = false;
			state.isProductDataFetched = true;
			state.error = action.payload;
		});
	},
});
export const { setCustomerProducts } = customerProductsSlice.actions;
export default customerProductsSlice.reducer;
